/**
 * Box Polygon Drawing Controller - handles box polygon drawing with area and distance measurements
 * User selects 2 points, creates a box polygon showing:
 * - Coordinates at each corner (4 points)
 * - Distance on each edge (in km)
 * - Area in center (Ha)
 */
import {Subject} from 'rxjs';
import {
  calculateAreaHa,
  calculateDistance,
  formatDistance,
  formatCoordinates
} from './drawing-calculations.util';
import {TranslateService} from '@ngx-translate/core';

export class DrawBoxPolygonController {
  private boxDrawingEnabled = false;
  private currentBox: any = null;
  private isDrawingBox = false;
  private map: any;
  private drawnItems: any;
  private boxStartPoint: any = null;
  private boxLayer: any = null;
  private boxMarkers: any[] = [];
  private boxLabels: any[] = [];

  // Real-time measurement labels
  private measurementLabels: any[] = [];

  // Event handlers stored for cleanup
  private mapClickHandler: any;
  private mapMouseMoveHandler: any;

  // Edit mode
  private editModeEnabled = false;
  private allDrawnShapes: Array<{
    layer: any;
    markers: any[];
    labels: any[];
    controlButtons?: any;
  }> = [];

  // Event emitter for shape creation
  public shapeCreated$ = new Subject<{
    type: 'box-polygon';
    coordinates: [number, number][];
    center: { lat: number; lng: number };
    area: number;
    layer: any;
  }>();

  private translateService: TranslateService;

  constructor(mapInstance: any, translateService: TranslateService) {
    this.translateService = translateService;
    this.map = mapInstance;
  }


  /**
   * Set the drawn items feature group
   */
  public setDrawnItems(items: any): void {
    this.drawnItems = items;
  }

  /**
   * Enable box polygon drawing mode
   */
  public enable(): void {
    if (this.boxDrawingEnabled) return;

    this.boxDrawingEnabled = true;
    this.map.getContainer().style.cursor = 'crosshair';
    this.map.getContainer().classList.add('drawing-mode-active');

    // Attach event listeners
    this.mapClickHandler = (e: any) => this.handleBoxClick(e);
    this.mapMouseMoveHandler = (e: any) => this.handleBoxMouseMove(e);

    this.map.on('click', this.mapClickHandler);
    this.map.on('mousemove', this.mapMouseMoveHandler);
  }

  /**
   * Disable box polygon drawing mode
   */
  public disable(): void {
    this.boxDrawingEnabled = false;
    this.cancelBoxDrawing();
    this.map.getContainer().style.cursor = '';
    this.map.getContainer().classList.remove('drawing-mode-active');

    // Remove event listeners
    if (this.mapClickHandler) {
      this.map.off('click', this.mapClickHandler);
    }
    if (this.mapMouseMoveHandler) {
      this.map.off('mousemove', this.mapMouseMoveHandler);
    }
  }

  /**
   * Check if box drawing is active
   */
  public isActive(): boolean {
    return this.boxDrawingEnabled;
  }

  /**
   * Handle box drawing click
   */
  private handleBoxClick(e: any): void {
    if (!this.boxDrawingEnabled) return;

    if (!this.boxStartPoint) {
      // First click - set start point
      this.boxStartPoint = e.latlng;
      this.isDrawingBox = true;
    } else {
      // Second click - create box
      const endPoint = e.latlng;
      this.createBoxPolygon(this.boxStartPoint, endPoint);
      this.boxStartPoint = null;
      this.isDrawingBox = false;
    }
  }

  /**
   * Handle box mouse move for preview
   */
  private handleBoxMouseMove(e: any): void {
    if (!this.boxDrawingEnabled || !this.isDrawingBox || !this.boxStartPoint) return;

    // Remove previous preview
    this.clearPreview();

    // Create preview box
    const bounds = (window as any).L.latLngBounds(this.boxStartPoint, e.latlng);
    this.boxLayer = (window as any).L.rectangle(bounds, {
      color: '#FF9800',
      weight: 2,
      opacity: 0.6,
      fill: true,
      fillColor: '#FF9800',
      fillOpacity: 0.2,
      dashArray: '5, 5'
    }).addTo(this.map);

    // Update real-time measurements
    this.updateRealTimeMeasurements(this.boxStartPoint, e.latlng);
  }

  /**
   * Update real-time measurement labels during box drawing
   */
  private updateRealTimeMeasurements(startPoint: any, endPoint: any): void {
    const L = (window as any).L;

    // Clear old measurement labels
    this.clearMeasurementLabels();

    // Calculate box corners
    const topLeft = {
      lat: Math.max(startPoint.lat, endPoint.lat),
      lng: Math.min(startPoint.lng, endPoint.lng)
    };
    const topRight = {
      lat: Math.max(startPoint.lat, endPoint.lat),
      lng: Math.max(startPoint.lng, endPoint.lng)
    };
    const bottomLeft = {
      lat: Math.min(startPoint.lat, endPoint.lat),
      lng: Math.min(startPoint.lng, endPoint.lng)
    };
    const bottomRight = {
      lat: Math.min(startPoint.lat, endPoint.lat),
      lng: Math.max(startPoint.lng, endPoint.lng)
    };

    const boxPoints = [topLeft, topRight, bottomRight, bottomLeft];

    // Calculate area
    const areaHa = calculateAreaHa(boxPoints);

    // Calculate width and height
    const width = calculateDistance(topLeft.lat, topLeft.lng, topRight.lat, topRight.lng);
    const height = calculateDistance(topLeft.lat, topLeft.lng, bottomLeft.lat, bottomLeft.lng);

    // Calculate center
    const centerLat = (startPoint.lat + endPoint.lat) / 2;
    const centerLng = (startPoint.lng + endPoint.lng) / 2;

    // Create area label at center
    const areaLabelContent = `
      <div style="
        background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
        color: white;
        padding: 10px 16px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 13px;
        font-weight: 600;
        white-space: nowrap;
        text-align: center;
        border: 2px solid rgba(255,255,255,0.3);
      ">
        <div style="font-size: 15px; margin-bottom: 4px;">${areaHa.toFixed(2)} Ha</div>
        <div style="font-size: 11px; opacity: 0.9; margin-bottom: 2px;">${this.translateService.instant('Width')}: ${formatDistance(width, this.translateService)}</div>
        <div style="font-size: 11px; opacity: 0.9;"> ${this.translateService.instant('Height')}: ${formatDistance(height, this.translateService)}</div>
      </div>
    `;

    const areaLabelIcon = L.divIcon({
      className: 'measurement-label area-label',
      html: areaLabelContent,
      iconSize: [150, 70],
      iconAnchor: [75, 35]
    });

    const areaLabel = L.marker([centerLat, centerLng], {
      icon: areaLabelIcon,
      interactive: false,
      zIndexOffset: 2000
    }).addTo(this.map);

    this.measurementLabels.push(areaLabel);

    // Width label at top
    const topMidLat = topLeft.lat;
    const topMidLng = (topLeft.lng + topRight.lng) / 2;

    const widthLabelContent = `
      <div style="
        background: rgba(76, 175, 80, 0.95);
        color: white;
        padding: 4px 10px;
        border-radius: 4px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        border: 1px solid rgba(255,255,255,0.4);
      ">
        ↔️ ${formatDistance(width, this.translateService)}
      </div>
    `;

    const widthLabelIcon = L.divIcon({
      className: 'measurement-label width-label',
      html: widthLabelContent,
      iconSize: [100, 25],
      iconAnchor: [50, 30]
    });

    const widthLabel = L.marker([topMidLat, topMidLng], {
      icon: widthLabelIcon,
      interactive: false,
      zIndexOffset: 1500
    }).addTo(this.map);

    this.measurementLabels.push(widthLabel);

    // Height label on left side
    const leftMidLat = (topLeft.lat + bottomLeft.lat) / 2;
    const leftMidLng = topLeft.lng;

    const heightLabelContent = `
      <div style="
        background: rgba(255, 152, 0, 0.95);
        color: white;
        padding: 4px 10px;
        border-radius: 4px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 12px;
        font-weight: 600;
        white-space: nowrap;
        border: 1px solid rgba(255,255,255,0.4);
      ">
        ↕️ ${formatDistance(height, this.translateService)}
      </div>
    `;

    const heightLabelIcon = L.divIcon({
      className: 'measurement-label height-label',
      html: heightLabelContent,
      iconSize: [100, 25],
      iconAnchor: [110, 12]
    });

    const heightLabel = L.marker([leftMidLat, leftMidLng], {
      icon: heightLabelIcon,
      interactive: false,
      zIndexOffset: 1500
    }).addTo(this.map);

    this.measurementLabels.push(heightLabel);

    // Add coordinate labels at corners
    const corners = [
      { point: topLeft, label: 'Top-Left', offsetX: 15, offsetY: 15 },
      { point: topRight, label: 'Top-Right', offsetX: -155, offsetY: 15 },
      { point: bottomRight, label: 'Bottom-Right', offsetX: -155, offsetY: -45 },
      { point: bottomLeft, label: 'Bottom-Left', offsetX: 15, offsetY: -45 }
    ];

    corners.forEach((corner) => {
      const coordLabelContent = `
        <div style="
          background: rgba(255, 152, 0, 0.95);
          color: white;
          padding: 4px 8px;
          border-radius: 3px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          font-family: 'Consolas', 'Courier New', monospace;
          font-size: 10px;
          font-weight: 500;
          white-space: nowrap;
          border: 1px solid rgba(255,255,255,0.4);
        ">
          <div style="opacity: 0.9;">${formatCoordinates(corner.point.lat, corner.point.lng)}</div>
        </div>
      `;

      const coordLabelIcon = L.divIcon({
        className: 'measurement-label coord-label',
        html: coordLabelContent,
        iconSize: [140, 20],
        iconAnchor: [-corner.offsetX, -corner.offsetY]
      });

      const coordLabel = L.marker([corner.point.lat, corner.point.lng], {
        icon: coordLabelIcon,
        interactive: false,
        zIndexOffset: 1000
      }).addTo(this.map);

      this.measurementLabels.push(coordLabel);
    });
  }

  /**
   * Clear all measurement labels
   */
  private clearMeasurementLabels(): void {
    this.measurementLabels.forEach(label => {
      if (label && this.map.hasLayer(label)) {
        this.map.removeLayer(label);
      }
    });
    this.measurementLabels = [];
  }

  /**
   * Create and store box polygon with measurements
   */
  private createBoxPolygon(startPoint: any, endPoint: any): void {
    const bounds = (window as any).L.latLngBounds(startPoint, endPoint);
    const L = (window as any).L;

    // Clear preview and measurement labels
    this.clearPreview();
    this.clearMeasurementLabels();

    // Create the 4 corners of the box
    const corners = [
      { lat: bounds.getNorth(), lng: bounds.getWest() }, // Top-left
      { lat: bounds.getNorth(), lng: bounds.getEast() }, // Top-right
      { lat: bounds.getSouth(), lng: bounds.getEast() }, // Bottom-right
      { lat: bounds.getSouth(), lng: bounds.getWest() }, // Bottom-left
    ];

    // Create box polygon
    const boxPolygon = L.polygon(corners, {
      color: '#FF9800',
      weight: 2,
      opacity: 0.8,
      fill: true,
      fillColor: '#FF9800',
      fillOpacity: 0.3
    }).addTo(this.map);

    // Calculate area
    const areaHa = calculateAreaHa(corners);

    // Add coordinate markers at each corner
    corners.forEach((corner, index) => {
      const marker = L.circleMarker(corner, {
        radius: 6,
        fillColor: '#FF9800',
        fillOpacity: 1,
        color: '#fff',
        weight: 2
      }).addTo(this.map);


      // Add coordinate label
      const coordLabelContent = `
        <div style="
          background: rgba(255, 152, 0, 0.95);
          color: white;
          padding: 4px 8px;
          border-radius: 3px;
          box-shadow: 0 2px 5px rgba(0,0,0,0.2);
          font-family: 'Consolas', 'Courier New', monospace;
          font-size: 10px;
          font-weight: 500;
          white-space: nowrap;
          border: 1px solid rgba(255,255,255,0.4);
        ">
          <div style="opacity: 0.9;">${formatCoordinates(corner.lat, corner.lng)}</div>
        </div>
      `;

      const coordLabel = L.marker(corner, {
        icon: L.divIcon({
          className: 'coordinate-label',
          html: coordLabelContent,
          iconSize: [140, 40],
          iconAnchor: [index === 0 || index === 3 ? -15 : 155, index < 2 ? 45 : -5]
        }),
        interactive: false
      }).addTo(this.map);

      this.boxMarkers.push(marker);
      this.boxLabels.push(coordLabel);
    });

    // Calculate and display edge distances
    const edges = [
      { start: corners[0], end: corners[1], label: 'Top' },
      { start: corners[1], end: corners[2], label: 'Right' },
      { start: corners[2], end: corners[3], label: 'Bottom' },
      { start: corners[3], end: corners[0], label: 'Left' }
    ];

    edges.forEach((edge, index) => {
      const distance = calculateDistance(edge.start.lat, edge.start.lng, edge.end.lat, edge.end.lng);
      const midLat = (edge.start.lat + edge.end.lat) / 2;
      const midLng = (edge.start.lng + edge.end.lng) / 2;

      const distanceLabelContent = `
        <div style="
          background: rgba(76, 175, 80, 0.95);
          color: white;
          padding: 3px 8px;
          border-radius: 4px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          font-family: 'Segoe UI', Arial, sans-serif;
          font-size: 11px;
          font-weight: 600;
          white-space: nowrap;
          border: 1px solid rgba(255,255,255,0.4);
        ">
          ${formatDistance(distance, this.translateService)}
        </div>
      `;

      const distanceLabel = L.marker([midLat, midLng], {
        icon: L.divIcon({
          className: 'distance-label',
          html: distanceLabelContent,
          iconSize: [80, 20],
          iconAnchor: [40, 10]
        }),
        interactive: false
      }).addTo(this.map);

      this.boxLabels.push(distanceLabel);
    });

    // Calculate and display area at center
    const center = bounds.getCenter();

    const areaLabelContent = `
      <div style="
        background: linear-gradient(135deg, #FF9800 0%, #F57C00 100%);
        color: white;
        padding: 10px 16px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        font-family: 'Segoe UI', Arial, sans-serif;
        font-size: 15px;
        font-weight: 600;
        text-align: center;
        border: 2px solid rgba(255,255,255,0.3);
      ">
        ${areaHa.toFixed(2)} ${this.translateService.instant('Ha')}
      </div>
    `;

    const areaLabel = L.marker(center, {
      icon: L.divIcon({
        className: 'area-label',
        html: areaLabelContent,
        iconSize: [150, 60],
        iconAnchor: [75, 30]
      }),
      interactive: false
    }).addTo(this.map);

    this.boxLabels.push(areaLabel);

    // Store markers and labels BEFORE resetting them
    const currentMarkers = [...this.boxMarkers];
    const currentLabels = [...this.boxLabels];

    this.currentBox = {
      layer: boxPolygon,
      bounds: bounds,
      coordinates: corners.map(c => [c.lat, c.lng]),
      markers: currentMarkers,
      labels: currentLabels,
      area: areaHa
    };

    // Emit shape created event
    this.shapeCreated$.next({
      type: 'box-polygon',
      coordinates: corners.map(c => [c.lat, c.lng]) as [number, number][],
      center: {
        lat: center.lat,
        lng: center.lng
      },
      area: areaHa,
      layer: boxPolygon
    });

    // Add to drawn items if available
    if (this.drawnItems) {
      this.drawnItems.addLayer(boxPolygon);
      currentMarkers.forEach(m => this.drawnItems.addLayer(m));
      currentLabels.forEach(l => this.drawnItems.addLayer(l));
    }

    // Store in allDrawnShapes immediately for proper tracking
    this.allDrawnShapes.push({
      layer: boxPolygon,
      markers: currentMarkers,
      labels: currentLabels
    });

    // Reset for next drawing
    this.boxMarkers = [];
    this.boxLabels = [];
  }

  /**
   * Clear preview layers
   */
  private clearPreview(): void {
    if (this.boxLayer) {
      this.map.removeLayer(this.boxLayer);
      this.boxLayer = null;
    }
    // Clear measurement labels when clearing preview
    this.clearMeasurementLabels();
  }

  /**
   * Cancel current box drawing
   */
  private cancelBoxDrawing(): void {
    this.clearPreview();

    if (this.currentBox && this.currentBox.layer) {
      this.map.removeLayer(this.currentBox.layer);
    }

    this.currentBox = null;
    this.boxStartPoint = null;
    this.isDrawingBox = false;
  }

  /**
   * Get current box
   */
  public getCurrentBox(): any {
    return this.currentBox;
  }

  /**
   * Enable edit mode - shows edit buttons for all drawn shapes
   */
  public enableEditMode(): void {
    if (this.editModeEnabled) return;
    this.editModeEnabled = true;

    // Store the current box as a drawn shape if it exists
    if (this.currentBox) {
      this.allDrawnShapes.push({
        layer: this.currentBox.layer,
        markers: this.currentBox.markers || [],
        labels: this.currentBox.labels || []
      });
    }

    // Add control buttons to all shapes
    this.allDrawnShapes.forEach((shape, index) => {
      this.addEditButtons(shape, index);
    });
  }

  /**
   * Disable edit mode - hides all edit buttons
   */
  public disableEditMode(): void {
    if (!this.editModeEnabled) return;
    this.editModeEnabled = false;

    // Remove all control buttons
    this.allDrawnShapes.forEach(shape => {
      this.removeEditButtons(shape);
    });
  }

  /**
   * Add edit buttons (Back and Remove) to a shape
   */
  private addEditButtons(shape: any, index: number): void {
    // COMMENTED OUT: Back and Remove button logic
    /*
    if (!shape.layer || shape.controlButtons) return;

    const L = (window as any).L;
    const bounds = shape.layer.getBounds();
    const center = bounds.getCenter();

    // Calculate position for buttons (top-right of the shape)
    const topRight = {
      lat: bounds.getNorth(),
      lng: bounds.getEast()
    };

    const buttonContainerHtml = `
      <div style="display: flex; gap: 4px;">
        <button class="shape-edit-btn shape-back-btn" style="
          background: #2196F3;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 600;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          transition: all 0.2s;
        ">
          ↶ Back
        </button>
        <button class="shape-edit-btn shape-remove-btn" style="
          background: #f44336;
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 11px;
          font-weight: 600;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
          transition: all 0.2s;
        ">
          🗑️ Remove
        </button>
      </div>
    `;

    const buttonIcon = L.divIcon({
      className: 'shape-edit-controls',
      html: buttonContainerHtml,
      iconSize: [180, 30],
      iconAnchor: [-10, 40]
    });

    const buttonMarker = L.marker([topRight.lat, topRight.lng], {
      icon: buttonIcon,
      interactive: true,
      zIndexOffset: 3000
    }).addTo(this.map);

    // Add event listeners after marker is added
    setTimeout(() => {
      const backBtn = buttonMarker.getElement()?.querySelector('.shape-back-btn');
      const removeBtn = buttonMarker.getElement()?.querySelector('.shape-remove-btn');

      if (backBtn) {
        backBtn.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          this.undoLastShape(index);
        });
        backBtn.addEventListener('mouseover', () => {
          (backBtn as HTMLElement).style.background = '#1976D2';
        });
        backBtn.addEventListener('mouseout', () => {
          (backBtn as HTMLElement).style.background = '#2196F3';
        });
      }

      if (removeBtn) {
        removeBtn.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          this.removeShape(index);
        });
        removeBtn.addEventListener('mouseover', () => {
          (removeBtn as HTMLElement).style.background = '#d32f2f';
        });
        removeBtn.addEventListener('mouseout', () => {
          (removeBtn as HTMLElement).style.background = '#f44336';
        });
      }
    }, 100);

    shape.controlButtons = buttonMarker;
    */
  }

  /**
   * Remove edit buttons from a shape
   */
  private removeEditButtons(shape: any): void {
    // COMMENTED OUT: Remove button logic
    /*
    if (shape.controlButtons) {
      this.map.removeLayer(shape.controlButtons);
      shape.controlButtons = null;
    }
    */
  }

  /**
   * Undo last shape (Back button action)
   */
  private undoLastShape(index: number): void {
    // COMMENTED OUT: Back button logic
    /*
    if (index === this.allDrawnShapes.length - 1) {
      // This is the last shape, remove it
      this.removeShape(index);
    }
    */
  }

  /**
   * Remove a specific shape and all its labels
   */
  private removeShape(index: number): void {
    // COMMENTED OUT: Remove shape logic
    /*
    const shape = this.allDrawnShapes[index];
    if (!shape) return;

    // Remove the layer
    if (shape.layer) {
      if (this.drawnItems && this.drawnItems.hasLayer(shape.layer)) {
        this.drawnItems.removeLayer(shape.layer);
      }
      if (this.map.hasLayer(shape.layer)) {
        this.map.removeLayer(shape.layer);
      }
    }

    // Remove all markers
    if (shape.markers) {
      shape.markers.forEach((marker: any) => {
        if (this.drawnItems && this.drawnItems.hasLayer(marker)) {
          this.drawnItems.removeLayer(marker);
        }
        if (this.map.hasLayer(marker)) {
          this.map.removeLayer(marker);
        }
      });
    }

    // Remove all labels (CRITICAL: ensure labels disappear)
    if (shape.labels) {
      shape.labels.forEach((label: any) => {
        if (this.drawnItems && this.drawnItems.hasLayer(label)) {
          this.drawnItems.removeLayer(label);
        }
        if (this.map.hasLayer(label)) {
          this.map.removeLayer(label);
        }
      });
    }

    // Remove control buttons
    this.removeEditButtons(shape);

    // Remove from array
    this.allDrawnShapes.splice(index, 1);

    // Re-add buttons with updated indices
    if (this.editModeEnabled) {
      this.allDrawnShapes.forEach((s, i) => {
        this.removeEditButtons(s);
        this.addEditButtons(s, i);
      });
    }
    */
  }

  /**
   * Register a newly drawn shape for edit mode tracking
   */
  public registerDrawnShape(layer: any, markers: any[], labels: any[]): void {
    const shape = {
      layer: layer,
      markers: markers || [],
      labels: labels || []
    };

    this.allDrawnShapes.push(shape);

    if (this.editModeEnabled) {
      this.addEditButtons(shape, this.allDrawnShapes.length - 1);
    }
  }

  /**
   * Clear all drawn shapes and their associated labels and markers
   */
  public clearAllShapes(): void {
    // Remove all shapes, markers, labels, and buttons that are tracked in allDrawnShapes
    this.allDrawnShapes.forEach(shape => {
      // Remove the layer
      if (shape.layer) {
        if (this.drawnItems && this.drawnItems.hasLayer(shape.layer)) {
          this.drawnItems.removeLayer(shape.layer);
        }
        if (this.map && this.map.hasLayer(shape.layer)) {
          this.map.removeLayer(shape.layer);
        }
      }

      // Remove all markers associated with THIS shape only
      if (shape.markers && Array.isArray(shape.markers)) {
        shape.markers.forEach((marker: any) => {
          if (this.drawnItems && this.drawnItems.hasLayer(marker)) {
            this.drawnItems.removeLayer(marker);
          }
          if (this.map && this.map.hasLayer(marker)) {
            this.map.removeLayer(marker);
          }
        });
      }

      // Remove all labels associated with THIS shape only
      if (shape.labels && Array.isArray(shape.labels)) {
        shape.labels.forEach((label: any) => {
          if (this.drawnItems && this.drawnItems.hasLayer(label)) {
            this.drawnItems.removeLayer(label);
          }
          if (this.map && this.map.hasLayer(label)) {
            this.map.removeLayer(label);
          }
        });
      }

      // Remove control buttons
      this.removeEditButtons(shape);
    });

    // Clear the array
    this.allDrawnShapes = [];

    // Clear current box if exists
    if (this.currentBox) {
      this.currentBox = null;
    }
  }
}
